<template>
  <section class="meeting-room">
    <div v-if="!loading" class="meeting-room__container">
      <div class="meeting-room__media-group">
        <div class="meeting-room__media-group-videos">
          <!-- Remote participant Media video -->
          <MediaVideo
            v-show="isRemoteConnected"
            class="m-2"
            :class="[mediaRemoteOnTop]"
            media-id="remote-media-div"
            width="145vmin"
            aspect="16:9"
            :participant-name="remoteParticipantName"
            participant-photo=" "
            :tag-name="remoteParticipantName"
            :mute-video="remoteVideoMuted"
            :mute-audio="remoteAudioMuted"
          />

          <!-- Local participant Media video -->
          <MediaVideo
            v-show="isLocalConnected"
            class="m-2"
            :class="[mediaVideoMinimized, mediaLocalOnTop, mediaFadeIn]"
            media-id="local-media"
            :width="localMediaVideoWidth"
            :aspect="localMediaAspectRatio"
            participant-photo=" "
            tag-name="Você"
            :mute-video="videoMuted"
            :mute-audio="audioMuted"
            :flipped="true"
            :style="mediaLocalOnTopStyles"
          />
        </div>

        <aside
          class="meeting-room__media-group-sidebar"
          :class="[sidebarShow, sidebarHide]"
        >
          <PatientRecordForm :room="room" @cancelMedicalRecord="hideSidebar" />
        </aside>
      </div>

      <div class="meeting-room__control-group">
        <button
          id="disableVideoBtn"
          class="media-button m-1"
          v-if="!videoMuted"
          @click.prevent="muteVideo"
        >
          <Icon :name="'camera-icon'" :width="20" :height="20" />
        </button>
        <button
          id="activeVideoBtn"
          class="media-button media-button--muted m-1"
          v-else
          @click.prevent="unmuteVideo"
        >
          <Icon :name="'camera-off-icon'" :width="20" :height="20" />
        </button>
        <button
          id="activeAudioBtn"
          class="media-button m-1"
          v-if="!audioMuted"
          @click.prevent="muteAudio"
        >
          <Icon :name="'mic-icon'" :width="20" :height="20" />
        </button>
        <button
          id="disableAudioBtn"
          class="media-button media-button--muted m-1"
          v-else
          @click.prevent="unmuteAudio"
        >
          <Icon :name="'mic-off-icon'" :width="20" :height="20" />
        </button>
        <button
          v-if="isDoctor"
          id="memedButton"
          class="media-button m-1"
          data-testid="prescription-button"
          :aria-busy="isOpeningPrescription ? 'true' : 'false'"
          @click="showPlataformaPrescricao"
        >
          <Icon
            :name="'medical-clipboard-icon'"
            :width="28"
            :height="28"
            style="stroke:#fff;"
          />
        </button>
        <button
          v-if="isDoctor"
          id="medicalRecordBtn"
          class="media-button m-1"
          @click="toggleSidebar"
        >
          <Icon :name="'clipboard-icon'" :width="28" :height="28" />
        </button>
        <button
          id="closeRoomBtn"
          class="media-button media-button-primary m-1"
          @click.prevent="disconnect"
        >
          <Icon :name="'cross-icon'" :width="20" :height="20" />
        </button>

        <b-tooltip
          v-if="!videoMuted"
          target="disableVideoBtn"
          placement="righttop"
        >
          Desativar câmara
        </b-tooltip>
        <b-tooltip
          v-if="videoMuted"
          target="activeVideoBtn"
          placement="righttop"
        >
          Ativar câmara
        </b-tooltip>
        <b-tooltip target="closeRoomBtn" placement="righttop">
          Finalizar chamada
        </b-tooltip>
        <b-tooltip
          v-if="!audioMuted"
          target="activeAudioBtn"
          placement="righttop"
        >
          Desativar microfone
        </b-tooltip>
        <b-tooltip
          v-if="audioMuted"
          target="disableAudioBtn"
          placement="righttop"
        >
          Ativar microfone
        </b-tooltip>
        <b-tooltip target="memedButton" placement="righttop">
          {{ prescriptionButtonLabel }}
        </b-tooltip>
        <b-tooltip target="medicalRecordBtn" placement="righttop">
          Prontuário médico
        </b-tooltip>
      </div>
    </div>
    <Loading :active.sync="loading" :can-cancel="false" :is-full-page="true" />

    <b-toast id="meetingRoomToast" no-auto-hide no-close-button>
      <div class="meeting-room__toaster">
        <div class="meeting-room__toaster-icon m-4">
          <img
            src="../assets/images/documents-illustration.svg"
            alt="documents"
          />
        </div>
        <div>
          <h4 class="meeting-room__toaster-title">
            Atualize o prontuário online
          </h4>
          <span class="meeting-room__toaster-description">
            Dê início ao preenchimento do prontuário médico para:
            <b>{{ room.patient.name }}</b>
          </span>
        </div>
        <div class="meeting-room__toaster-button-group m-2">
          <Button variant="primary" size="md" @click.native="toggleSidebar">
            Preencher agora
          </Button>
          <Button
            variant="link"
            size="md"
            @click.native="$bvToast.hide('meetingRoomToast')"
          >
            Preencher depois
          </Button>
        </div>
      </div>
    </b-toast>

    <button
      v-show="isPrescriptionOpen"
      id="closePrescriptionBtn"
      class="close-memed-btn"
      @click="hideMemedPrescription"
    >
      <Icon :name="'cross-icon'" :width="16" :height="16" />
    </button>

    <div v-if="isPrescriptionOpen" class="prescription-modal" />
  </section>
</template>

<script>
import {
  roomMethods,
  userMethods,
  prescriptionMethods
} from "./../state/helpers";
import {
  connect,
  createLocalTracks,
  createLocalVideoTrack
} from "twilio-video";
import Icon from "../components/CustomIcons";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import dePara from "../utils/drugs";
import { ensureMemedSdk, memedMessageFor } from "../utils/memed";
import { resolveMemedToken } from "../utils/memedToken";
import MediaVideo from "../components/MediaVideo";
import PatientRecordForm from "../components/PatientRecordForm";
import Button from "../components/Button";
import { isDoctor } from "../utils/userRole";

export default {
  name: "MeetingRoom",
  components: {
    Icon,
    Loading,
    MediaVideo,
    PatientRecordForm,
    Button
  },
  data() {
    return {
      loading: true,
      audioMuted: false,
      videoMuted: false,
      remoteAudioMuted: false,
      remoteVideoMuted: false,
      room: {},
      roomInstance: {},
      localTracks: {},
      isRemoteConnected: false,
      isLocalConnected: false,
      memedPatient: null,
      memedHub: null,
      isOpeningPrescription: false,
      prescriptionListenerReady: false,
      isSidebarHidden: true,
      isPrescriptionOpen: false
    };
  },
  mounted() {
    this.getRoomDetails(this.$route.params.room).then(response => {
      this.room = response.data;
      this.showSymptoms({ id: response.data.patient.id })
        .then(symptomsResponse => {
          this.memedPatient = {
            ...symptomsResponse.data.user,
            id: response.data.patient.id
          };
        })
        .catch(error => console.log(error));
      this.openRoom();
      this.loading = false;
    });
    this.audioMuted = this.$route.params.audioMuted;
    this.videoMuted = this.$route.params.videoMuted;
    window.addEventListener("beforeunload", this.leaveComfirm);
    // if(this.isDoctor) {
    //   this.makeCustomToast();
    // }
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.leaveComfirm);
    clearInterval(this.interval);
  },
  methods: {
    ...roomMethods,
    ...userMethods,
    ...prescriptionMethods,
    // O SDK da Memed é carregado AQUI, no clique, e não mais no login: ele
    // instala iframes e um listener global de `message` no documento, então
    // não pode subir em telas que não vão prescrever.
    async ensurePrescriptionSdk() {
      const token = await resolveMemedToken(this.$store.state.user.currentUser);
      this.memedHub = await ensureMemedSdk(token);
      return this.memedHub;
    },
    async showPlataformaPrescricao() {
      // O botão continua sempre habilitado; a guarda é só contra clique duplo
      // enquanto o SDK carrega.
      if (this.isOpeningPrescription) return;
      this.isOpeningPrescription = true;

      try {
        await this.ensurePrescriptionSdk();
        this.registerPrescriptionListener();
        await this.sendPrescriptionCommands();
      } catch (error) {
        this.$toast.error(memedMessageFor(error));
      } finally {
        this.isOpeningPrescription = false;
      }
    },
    registerPrescriptionListener() {
      if (this.prescriptionListenerReady) return;
      this.prescriptionListenerReady = true;

      this.memedHub.event.add("prescricaoImpressa", prescriptionData => {
        const data = {
          prescription: {
            appointment_id: this.room.appointment_id,
            lme: prescriptionData.prescricao.lme,
            memed_id: prescriptionData.prescricao.id,
            signed: prescriptionData.prescricao.signed,
            drugs_attributes: dePara(prescriptionData.prescricao.medicamentos)
          }
        };
        return this.postPrescription(data);
      });
    },
    sendPrescriptionCommands() {
      return this.memedHub.command
        .send("plataforma.prescricao", "setPaciente", {
          nome: this.memedPatient.name,
          telefone: this.memedPatient.phone_number,
          dificuldade_locomocao: true,
          idExterno: this.memedPatient.id
        })
        .then(() => {
          this.memedHub.command.send(
            "plataforma.prescricao",
            "setFeatureToggle",
            {
              deletePatient: false,
              removePatient: false,
              editPatient: false
            }
          );
          this.memedHub.module.show("plataforma.prescricao");
          this.memedHub.module.updateStyle({
            moduleName: "plataforma.prescricao",
            styles: {
              zIndex: 1,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "960px"
            }
          });
          this.isPrescriptionOpen = true;
        });
    },
    muteAudio() {
      this.roomInstance.localParticipant.audioTracks.forEach(publication => {
        publication.track.disable();
        this.audioMuted = true;
      });
    },
    unmuteAudio() {
      this.roomInstance.localParticipant.audioTracks.forEach(publication => {
        publication.track.enable();
        this.audioMuted = false;
      });
    },
    muteVideo() {
      this.roomInstance.localParticipant.videoTracks.forEach(publication => {
        publication.track.disable();
        this.videoMuted = true;
      });
    },
    unmuteVideo() {
      this.roomInstance.localParticipant.videoTracks.forEach(publication => {
        publication.track.enable();
        this.videoMuted = false;
      });
    },
    disconnect() {
      swal.fire({
        title: "Finalizar!",
        text: "Deseja finalizar esta chamada?",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#ED135A",
        cancelButtonColor: "#cb6262",
        confirmButtonText: "Sim, Finalizar!",
        cancelButtonText: "Cancelar",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          window.removeEventListener("beforeunload", this.leaveComfirm);
          this.cleanRemoteMediaDiv();
          this.roomInstance.localParticipant.tracks.forEach(publication => {
            publication.track.stop();
          });
          this.$set(this, "roomInstance", {});
          this.$router.push(
            {
              name: "ConsultFinished",
              params: {
                room: this.room.id
              }
            },
            () => {
              this.$router.go(0);
            }
          );
        }
      });
    },
    openRoom() {
      createLocalTracks({
        audio: true,
        video: true
      })
        .then(localTracks => {
          this.localTracks = localTracks;
          return connect(this.room.room_access_token, {
            name: "my-room-name",
            tracks: localTracks
          });
        })
        .then(room => this.activeRoom(room));
    },
    activeRoom(room) {
      room.localParticipant.tracks.forEach(publication => {
        if (publication.kind === "video")
          document
            .getElementById("local-media")
            .appendChild(publication.track.attach());

        this.isLocalConnected = true;
      });

      this.$set(this, "roomInstance", room);

      const handleRemoteTrackEvents = track => {
        track.on("disabled", () => {
          if (track.kind === "video") {
            this.remoteVideoMuted = true;
          } else {
            this.remoteAudioMuted = true;
          }
        });
        track.on("enabled", () => {
          if (track.kind === "video") {
            this.remoteVideoMuted = false;
          } else {
            this.remoteAudioMuted = false;
          }
        });
      };

      room.participants.forEach(participant => {
        if (participant.identity) this.isRemoteConnected = true;

        participant.tracks.forEach(publication => {
          if (publication.track) {
            document
              .getElementById("remote-media-div")
              .appendChild(publication.track.attach());
          }
          if (publication.isSubscribed) {
            handleRemoteTrackEvents(publication.track);
          }
          publication.on("subscribed", handleRemoteTrackEvents);
        });

        participant.on("trackSubscribed", track => {
          document
            .getElementById("remote-media-div")
            .appendChild(track.attach());
        });
      });

      room.on("participantConnected", participant => {
        if (participant.identity) this.isRemoteConnected = true;

        participant.tracks.forEach(publication => {
          if (publication.isSubscribed) {
            const track = publication.track;
            document
              .getElementById("remote-media-div")
              .appendChild(track.attach());
          }
          if (publication.isSubscribed) {
            handleRemoteTrackEvents(publication.track);
          }
          publication.on("subscribed", handleRemoteTrackEvents);
        });

        participant.on("trackSubscribed", track => {
          document
            .getElementById("remote-media-div")
            .appendChild(track.attach());
        });
      });

      room.on("participantDisconnected", participant => {
        if (
          participant.state === "disconnected" &&
          participant.identity != room.localParticipant
        ) {
          this.isRemoteConnected = false;
          this.cleanRemoteMediaDiv();
        }
      });

      if (this.audioMuted) this.muteAudio();
      if (this.videoMuted) this.muteVideo();
    },
    cleanRemoteMediaDiv() {
      document.getElementById("remote-media-div").innerHTML = "";
    },
    leaveComfirm(event) {
      const warning = "Você deseja realmente sair?";
      event.returnValue = warning;
      return warning;
    },
    toggleSidebar() {
      this.isSidebarHidden = !this.isSidebarHidden;
      this.$bvToast.hide("meetingRoomToast");
    },
    hideSidebar() {
      this.isSidebarHidden = true;
    },
    hideMemedPrescription() {
      // O "x" estourava quando o SDK nunca chegou a carregar.
      if (this.memedHub) this.memedHub.module.hide("plataforma.prescricao");
      this.isPrescriptionOpen = false;
    },
    makeCustomToast() {
      setTimeout(() => {
        this.$bvToast.show("meetingRoomToast");
        let BToaster = document.querySelector(".b-toaster");
        BToaster.classList.add("meeting-room--toast");
      }, 5000);
    }
  },

  computed: {
    // O botão aqui é só ícone: o sinal de carregando vai no aria-busy (com
    // estilo) e no tooltip.
    prescriptionButtonLabel() {
      return this.isOpeningPrescription ? "Carregando…" : "Prescrição médica";
    },

    connected() {
      return !!this.roomInstance.sid;
    },
    mutedClass() {
      return this.audioMuted ? "meeting-room__muted" : " ";
    },
    isDoctor() {
      return isDoctor(this.$store.state.user.currentUser);
    },
    remoteParticipantName() {
      return this.isDoctor ? this.room.patient.name : this.room.doctor.name;
    },
    remoteParticipantPhoto() {
      return this.isDoctor ? this.room.patient.photo : this.room.doctor.photo;
    },
    mediaVideoMinimized() {
      return {
        "media-video-minimized": this.isRemoteConnected && this.isSidebarHidden
      };
    },
    mediaRemoteOnTop() {
      return { "media-remote-on-top": this.isPrescriptionOpen };
    },
    mediaLocalOnTop() {
      return { "media-local-on-top": this.isPrescriptionOpen };
    },
    mediaLocalOnTopStyles() {
      return this.isPrescriptionOpen
        ? { top: this.isRemoteConnected ? "150px" : "0" }
        : {};
    },
    localMediaVideoWidth() {
      return !this.isRemoteConnected ? "145vmin" : "65vmin";
    },
    localMediaAspectRatio() {
      return !this.isRemoteConnected ? "16:9" : "8:5";
    },
    sidebarShow() {
      return { "sidebar-visible": !this.isSidebarHidden };
    },
    sidebarHide() {
      return { "sidebar-hidden": this.isSidebarHidden };
    },
    mediaFadeIn() {
      return { "media-faded": !this.isSidebarHidden && this.isRemoteConnected };
    }
  }
};
</script>

<style>
#memedButton[aria-busy="true"] {
  opacity: 0.6;
  cursor: progress;
}

.prescription-modal {
  position: absolute;
  background-color: rgba(128, 128, 128, 0.96);
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
}

.close-memed-btn {
  position: absolute;
  background-color: transparent;
  border: none;
  right: 0;
  top: 0;
  z-index: 1001;
}

.close-memed-btn:hover,
.close-memed-btn:focus {
  background-color: transparent;
  border: none;
}
</style>
